import { CameraPlatformSchema } from '../../../../renderer/src/camera-platform-contract';
import { useEffect, useRef, useState } from 'react';
import { z } from 'zod';
import { CAMERA_DISTANCE_IDS, DEFAULT_CAMERA_DISTANCE_ID } from '../../domain/camera-distance';
import { resolveCameraKeyframes } from '../../domain/camera-keyframes';
import { CAMERA_GIMBAL_IDS } from '../../../../renderer/src/camera-gimbal';
import { SOCIAL_TARGETS } from '../../../../renderer/src/video-presentation';
import { SOCIAL_LAYOUTS } from '../../../../renderer/src/social-presentation';
import { DEFAULT_DEPTH_OF_FIELD } from '../../../../renderer/src/depth-of-field';
import { MapStyleSchema } from '../../../../renderer/src/map-style-contract';
import { SAMPLE_DURATION_SECONDS, SAMPLE_OVERLAY_OPACITY } from './settings';

export const SAMPLE_SESSION_KEY = 'overland-traces.sample-explorer.v1';
const schema = z.object({
  version: z.literal(1), social: z.boolean(),
  map: MapStyleSchema,
  rig: z.object({ camera: CameraPlatformSchema, distance: z.enum(CAMERA_DISTANCE_IDS),
    cameraAngle: z.number().min(0).max(90).nullable(), cameraGimbal: z.enum(CAMERA_GIMBAL_IDS),
    depthOfField: z.object({ enabled: z.boolean(), method: z.enum(['depth', 'band']), blur: z.number().min(0).max(32),
      width: z.number().min(0).max(1), fadeTopDown: z.boolean(), focus: z.enum(['rider', 'center']) }) }),
  target: z.enum(SOCIAL_TARGETS), layout: z.enum(SOCIAL_LAYOUTS), overlayOpacity: z.number().min(0).max(100),
  mode: z.enum(['clean', 'platform', 'guides']), progress: z.number().min(0).max(1),
  playbackRate: z.union([z.literal(.25), z.literal(.5), z.literal(1)]).default(.25),
  tracks: z.unknown().optional().transform(value => value === undefined ? undefined : resolveCameraKeyframes(value, SAMPLE_DURATION_SECONDS)),
});
export type SampleExplorerSession = z.infer<typeof schema>;

/** Restore this tab's comparison after a browser reload; never restore playing state. */
export function readSampleExplorerSession(): SampleExplorerSession | undefined {
  try {
    const raw = sessionStorage.getItem(SAMPLE_SESSION_KEY);
    return raw ? schema.parse(JSON.parse(raw)) : undefined;
  } catch { return undefined; }
}

export function useSampleExplorerSession(initialSocial: boolean) {
  const [state, setState] = useState<SampleExplorerSession>(() => readSampleExplorerSession() ?? {
    version: 1, social: initialSocial, map: 'topographic', rig: { camera: 'aerial-follow', distance: DEFAULT_CAMERA_DISTANCE_ID,
      cameraAngle: null, cameraGimbal: 'glide', depthOfField: { ...DEFAULT_DEPTH_OF_FIELD } },
    target: 'instagram-reels', layout: 'lower-title', overlayOpacity: SAMPLE_OVERLAY_OPACITY,
    mode: 'clean', progress: .5, playbackRate: .25, tracks: undefined,
  });
  const latest = useRef(state); latest.current = state;
  const save = () => { try { sessionStorage.setItem(SAMPLE_SESSION_KEY, JSON.stringify(latest.current)); } catch { /* Storage may be unavailable in private browsing. */ } };
  useEffect(() => {
    const timer = setTimeout(save, 250);
    return () => clearTimeout(timer);
  }, [state]);
  useEffect(() => {
    const hidden = () => { if (document.hidden) save(); };
    window.addEventListener('pagehide', save); document.addEventListener('visibilitychange', hidden);
    return () => { save(); window.removeEventListener('pagehide', save); document.removeEventListener('visibilitychange', hidden); };
  }, []);
  const patch = (change: Partial<SampleExplorerSession>) => setState(current => ({ ...current, ...change }));
  return [state, patch] as const;
}
